// Shared enum → Mongolian label maps for the tenant fee-policy screens (spec §5).
// Single source of truth so no technical enum (ORDER_TOTAL, AUTO_DEDUCT, PRO_RATA,
// PERCENTAGE, …) ever leaks to the user. Use the `*Label` helpers, which fall back
// to the raw value for any unmapped/legacy code rather than rendering blank.

import type { FeePolicy, FeePolicyTier } from "../apis/tenantFeePolicy";
import { formatMoneyCompact, formatPercent } from "./format";

export const CALC_METHOD_LABELS: Record<string, string> = {
  PERCENTAGE: "Хувиар",
  FLAT      : "Тогтмол дүн",
  TIERED    : "Шатлалаар",
};

// Headline label shown above the big fee figure on the policy cards. Method-aware
// so the number underneath is self-explanatory (no separate "Хувиар" caption).
export const HEADLINE_LABELS: Record<string, string> = {
  PERCENTAGE: "Шимтгэлийн хувь",
  FLAT      : "Тогтмол шимтгэл",
  TIERED    : "Шатлалсан шимтгэл",
};

// API enum values are ORDER_TOTAL | PAYMENT | NET | SUBTOTAL_EX_TAX. The legacy
// spec spellings (PAYMENT_AMOUNT / NET_AMOUNT / SUBTOTAL) are mapped too for safety.
export const FEE_BASIS_LABELS: Record<string, string> = {
  ORDER_TOTAL    : "Захиалгын нийт дүн",
  PAYMENT        : "Төлбөрийн дүн",
  PAYMENT_AMOUNT : "Төлбөрийн дүн",
  NET            : "Цэвэр дүн",
  NET_AMOUNT     : "Цэвэр дүн",
  SUBTOTAL_EX_TAX: "Татвар хассан",
  SUBTOTAL       : "Татвар хассан",
};

export const DEDUCTION_MODE_LABELS: Record<string, string> = {
  AUTO_DEDUCT     : "Төлбөрөөс автоматаар суутгах",
  SEPARATE_INVOICE: "Тусдаа нэхэмжлэх",
  OFFSET_PAYOUT   : "Тооцооноос суутгах",
  EXTERNAL        : "Системээс гадуур",
  TRACK_ONLY      : "Системээс гадуур",
};

export const REFUND_POLICY_LABELS: Record<string, string> = {
  PRO_RATA : "Хувь тэнцүүлэн буцаах",
  FULL_ONLY: "Зөвхөн бүрэн буцаалтад",
  NONE     : "Шимтгэл буцаахгүй",
};

export const SCOPE_LABELS: Record<string, string> = {
  DEFAULT: "Үндсэн",
  NAMED  : "Нэрлэсэн",
};

export const STATUS_LABELS: Record<string, string> = {
  DRAFT    : "Ноорог",
  SCHEDULED: "Товлосон",
  ACTIVE   : "Идэвхтэй",
  SUSPENDED: "Түр зогссон",
  ARCHIVED : "Архивласан",
};

const lookup = (map: Record<string, string>, key: string | null | undefined): string =>
  key == null ? "—" : map[key] ?? key;

export const calcMethodLabel = (v: string | null | undefined) => lookup(CALC_METHOD_LABELS, v);
export const headlineLabel = (v: string | null | undefined) => lookup(HEADLINE_LABELS, v);

// --- Tiered-fee display helpers -------------------------------------------------
// tierConfig comes back either as a bare array or wrapped as { tiers: [...] }.
export function readTiers(policy: Pick<FeePolicy, "tierConfig">): FeePolicyTier[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw: any = policy.tierConfig;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.tiers)) return raw.tiers;
  return [];
}

/** Range label: "0 – 1сая₮"; open-ended last tier → "5сая₮-аас дээш". */
export function formatTierRange(tier: FeePolicyTier, currency = "MNT"): string {
  const from = formatMoneyCompact(Number(tier.fromAmount) || 0, currency);
  if (tier.toAmount == null || tier.toAmount === "") return `${from}-аас дээш`;
  return `${from} – ${formatMoneyCompact(tier.toAmount, currency)}`;
}

/** tier.rate is a decimal fraction (0.01 = 1%) → "1.00%". */
export const formatTierRate = (tier: FeePolicyTier): string => formatPercent(Number(tier.rate) * 100);

export const feeBasisLabel = (v: string | null | undefined) => lookup(FEE_BASIS_LABELS, v);
export const deductionModeLabel = (v: string | null | undefined) => lookup(DEDUCTION_MODE_LABELS, v);
export const refundPolicyLabel = (v: string | null | undefined) => lookup(REFUND_POLICY_LABELS, v);
export const scopeLabel = (v: string | null | undefined) => lookup(SCOPE_LABELS, v);
export const statusLabel = (v: string | null | undefined) => lookup(STATUS_LABELS, v);
